import { Article } from '@wix/answers-api';
import { Box, Search } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import React, { FC, useEffect, useRef } from 'react';

import { SitePickerSiteListItem } from './SitePickerSiteListItem';
import { SEARCH_DEBOUNCE } from '../../../../../../constants';
import { DATA_HOOKS } from '../../../../../../dataHooks';
import { useUserSitesRemoteSearch } from '../../../../../../hooks/useUserSitesRemoteSearch';
import { useArticleUnifiedBi } from '../../../../../../utils/article-unified-bi';
import { useStore } from '../../../../../../zustand/stores';
import { MAX_QUERY_LENGTH } from '../../../../../SiteSelector/SiteSelectorDropDown';
import {
  SiteSelectorState,
} from '../../../../../SiteSelector/SiteSelectorStates';

import css from './index.module.scss';

export type SiteListProps = {
  article: Article;
  isModalOpen: boolean;
  isLoadingSites: boolean;
  modalSessionId: string;
  onClose: () => void;
};

export const SiteList: FC<SiteListProps> = ({
  article,
  isLoadingSites,
  onClose,
  isModalOpen,
  modalSessionId,
}) => {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const { selectedSite } = useStore((state) => state.siteSelectorSlice);
  const { sitePickerModalLoadTrack } = useArticleUnifiedBi(article);
  const hasFiredLoadRef = useRef(false);
  const {
    query,
    setQuery,
    sites: remoteSites,
    loading,
    overlay,
    loadMore,
    hasMore,
    clearSearch,
  } = useUserSitesRemoteSearch({ enabled: isModalOpen });

  useEffect(() => {
    if (isModalOpen) {
      void clearSearch();
    } else {
      hasFiredLoadRef.current = false;
    }
  }, [isModalOpen, clearSearch]);

  useEffect(() => {
    if (
      isModalOpen &&
      modalSessionId &&
      !hasFiredLoadRef.current &&
      !loading &&
      !overlay &&
      remoteSites.length > 0
    ) {
      hasFiredLoadRef.current = true;
      void sitePickerModalLoadTrack({
        msid: selectedSite?.metaSiteId,
        count: remoteSites.length,
        modalSessionId,
      });
    }
  }, [
    isModalOpen,
    modalSessionId,
    loading,
    overlay,
    remoteSites.length,
    selectedSite,
    sitePickerModalLoadTrack,
  ]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !hasMore) {
      return;
    }
    const onScroll = () => {
      if (loading) {
        return;
      }
      const nearBottom =
        el.scrollHeight - el.scrollTop - el.clientHeight < 80;
      if (nearBottom) {
        void loadMore();
      }
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [hasMore, loadMore, loading]);

  const searchBusy = loading || isLoadingSites;

  return (
    <Box direction="vertical" className={css.siteList}>
      <Box
        verticalAlign="middle"
        boxSizing="border-box"
        className={css.searchContainer}
      >
        <Search
          size="medium"
          placeholder={t('site-selector-modal.search')}
          onClear={() => setQuery('')}
          dataHook={DATA_HOOKS.SITE_SELECTOR_SEARCH}
          onChange={(e) => setQuery(e.target.value)}
          value={query}
          status={searchBusy ? 'loading' : undefined}
          debounceMs={SEARCH_DEBOUNCE}
          maxLength={MAX_QUERY_LENGTH}
        />
      </Box>
      {overlay ? (
        <SiteSelectorState
          type={overlay}
          query={query}
        />
      ) : (
        <div ref={scrollRef} className={css.sitesContainer}>
          <div className={css.sitesScrollInner}>
            {remoteSites.map((site, index) => (
              <SitePickerSiteListItem
                key={site.metaSiteId}
                article={article}
                site={site}
                modalSessionId={modalSessionId}
                onClose={onClose}
                showDivider={index !== remoteSites.length - 1}
              />
            ))}
          </div>
        </div>
      )}
    </Box>
  );
};
